// ============================================
// LIGA AJUPAM - withTimeout
// ============================================
// El cliente de Supabase (y fetch) no tienen timeout por defecto: una .rpc(),
// .functions.invoke() o fetch() contra una conexión colgada puede quedar
// esperando para siempre. Cuando ese await alimenta un loader (splash de
// arranque, spinner de "CONTINUAR", validación de sesión), la UI queda
// "pensando" sin salida.
//
// `withTimeout` corta la espera a los `ms` indicados rechazando con
// TimeoutError. La promesa original sigue viva (una Promise no se puede
// cancelar), pero el caller deja de esperarla y puede degradar a un estado
// usable (error + reintento, login, etc.) en vez de colgarse.

export class TimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`Timeout (${ms}ms): ${label}`);
    this.name = 'TimeoutError';
  }
}

export const isTimeoutError = (e: unknown): e is TimeoutError =>
  e instanceof TimeoutError || (e as { name?: string })?.name === 'TimeoutError';

/**
 * Envuelve una promesa con un timeout. Rechaza con TimeoutError si `promise`
 * no se resuelve/rechaza dentro de `ms`.
 *
 * @param promise  la promesa a acotar
 * @param ms       milisegundos antes de cortar
 * @param label    nombre para el mensaje de error / logs
 */
// Acepta `PromiseLike<T>` (no sólo `Promise<T>`) para poder envolver los query
// builders de Supabase (PostgrestFilterBuilder/PostgrestBuilder son thenables,
// no Promises) sin castear en cada call-site.
export function withTimeout<T>(promise: PromiseLike<T>, ms: number, label = 'operación'): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}
